/**
 * 로그인 페이지 — ID/비밀번호 입력.
 *
 * 응답 분기:
 *   - basic    → 즉시 메인으로 이동
 *   - mfa 필요 → /login/totp 로 이동 (challengeId, offerTrustDevice 전달)
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ApiCallError } from '../lib/api';
import * as authApi from '../lib/auth-api';
import { useAuth } from '../lib/auth-context';

export function LoginPage() {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * 로그인 뒤 돌아갈 주소.
   *
   * ⭐⭐**물음표(search)·닻(hash)까지 챙긴다** (v2.198.0). 종전에는 `pathname` 만 챙겨서
   *   메신저로 보낸 딥링크가 로그인을 거치면 **걸러진 화면이 아니라 첫 화면**으로 떨어졌다
   *   (딜 만료 알림의 `?expiring=1` · 딜 딥링크 `?deal=` 이 그 자리였다).
   *   ProtectedRoute 는 `location` 을 통째로 넘겨 주고 있었다 — 받는 쪽만 버리고 있었던 것이다.
   */
  const fromLoc = (location.state as {
    from?: { pathname: string; search?: string; hash?: string };
  } | null)?.from;
  // 8시간 안 써서 화면이 스스로 로그아웃했을 때 (v2.231.0 · F-S7 · components/IdleLogout.tsx)
  const idleLogout = !!(location.state as { idleLogout?: boolean } | null)?.idleLogout;
  const from = fromLoc
    ? `${fromLoc.pathname}${fromLoc.search ?? ''}${fromLoc.hash ?? ''}`
    : '/';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.login(userId, password);
      if (result.kind === 'session') {
        setUser(result.user);
        navigate(from, { replace: true });
      } else {
        // mfa 필요 — TOTP 화면으로
        navigate('/login/totp', {
          replace: true,
          state: {
            challengeId: result.challengeId,
            offerTrustDevice: result.offerTrustDevice,
            redirectTo: from,
          },
        });
      }
    } catch (e) {
      const msg = e instanceof ApiCallError ? e.message : '로그인 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8"
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold">SM 포털</h1>
          <p className="mt-1 text-xs text-slate-500">로그인</p>
        </div>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">아이디</span>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            autoComplete="username"
            // 폰이 첫 글자를 대문자로 바꾸지 않게 (v2.232.0 · 점검 ③ F-S3 · 서버도 소문자로 맞춘다)
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            required
            className="ui-input mt-1 w-full"
          />
        </label>

        <label className="block mb-3">
          <span className="text-sm text-slate-700">비밀번호</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="ui-input mt-1 w-full"
          />
        </label>

        {idleLogout && !error && (
          <div className="mb-3 rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            8시간 동안 쓰지 않아 자동으로 로그아웃되었습니다. 다시 로그인해 주세요.
          </div>
        )}

        {error && (
          <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full"
        >
          {submitting ? '확인 중…' : '로그인'}
        </button>

        <div className="mt-4 text-center">
          <Link to="/password-reset" className="text-xs text-slate-500 hover:text-blue-600 hover:underline">
            비밀번호를 잊으셨나요?
          </Link>
        </div>
      </form>
    </div>
  );
}
